/**
 * @fileoverview 前端业务自动上报：页面可见性 / DOM 数量 / JS 内存
 *
 * 与原 web/accounts 实现的差异：不再依赖全局 `$logger` 单例，
 * 而是接收 logger 实例（通过 createLogger 创建），由 core 显式调用并管理清理。
 * 这样同一页面可存在多个独立 logger 实例（micro-app 场景），互不污染。
 */
import type { AliWebLogger } from './AliWebLogger'

import { CommonInfoAction, CommonWarnAction } from '../actions/common'

const LEAVE_TIME = 1000 * 60 * 60 * 12 // 12 小时

/** 允许最大 JS 堆内存 1024M */
const MAX_MEMORY_ALLOW = 1024 * 1024 * 1024
/** 最大内存使用率 60% */
const MAX_MEMORY_PERCENT_ALLOW = 60
/** 最大 element 数量 15000 */
const MAX_ELEMENT_ALLOW = 15000

/** 页面可见性切换的上报配置 */
export interface VisibilityChangeOptions {
  /** 离开超过此时长(ms)后回来，触发强制刷新，默认 12 小时 */
  leaveTime?: number
}

/**
 * 监听页面可见性变化
 * @description 页面隐藏后若超过 leaveTime 再回来，强制刷新（避免长时间挂起的陈旧会话）
 * @returns 清理函数（取消对内部状态的占用）
 */
export function createVisibilityHandler(logger: AliWebLogger, options: VisibilityChangeOptions = {}) {
  const { leaveTime = LEAVE_TIME } = options
  let beforeLeaveTime = 0

  return function visibilityChange() {
    if (document.visibilityState === 'hidden') {
      beforeLeaveTime = Date.now()
      return
    }
    if (beforeLeaveTime && Date.now() - beforeLeaveTime > leaveTime) {
      logger.info(CommonInfoAction.PAGE_LEAVE_12H, { duration: Date.now() - beforeLeaveTime })
      logger.log('页面刷新', { msg: '页面离开超过12小时，强制刷新页面' }, { immediate: true })
      window.location.reload()
    }
  }
}

/**
 * 监听页面 DOM 元素数量与 JS 内存使用情况
 * @description 每分钟轮询一次，超过阈值上报 warn/info
 * @returns 清理函数（清除定时器）
 */
export function createMemoryHandler(logger: AliWebLogger) {
  let memoryInterval = 0
  let appContainer: Element | null = null

  const intervalFn = () => {
    if (document.visibilityState === 'hidden') return
    if (!appContainer) {
      appContainer = document.querySelector('.app-container')
    }
    const elList = appContainer?.getElementsByTagName('*') || []

    if (elList.length > MAX_ELEMENT_ALLOW) {
      logger.warn(CommonWarnAction.PAGE_DOM_ELEMENT_TOO_MANY, { count: elList.length })
    }

    const { jsHeapSizeLimit, usedJSHeapSize } = window.performance.memory || {}
    if (!jsHeapSizeLimit || !usedJSHeapSize) return

    const ratio = Number((usedJSHeapSize / jsHeapSizeLimit).toFixed(3))

    if (usedJSHeapSize > MAX_MEMORY_ALLOW) {
      logger.info(CommonInfoAction.JS_MEMORY_USED_1024M, { used: usedJSHeapSize, limit: jsHeapSizeLimit })
    }
    if (ratio * 100 > MAX_MEMORY_PERCENT_ALLOW) {
      logger.info(CommonInfoAction.JS_MEMORY_USED_RATE_OVER_LIMIT, { used: usedJSHeapSize, ratio })
    }
  }

  // TODO: setInterval 计时可能不准确（原逻辑遗留，保持行为一致）
  memoryInterval = window.setInterval(intervalFn, 60 * 1000)
  // 立即执行一次，收集页面初始化状态
  window.requestIdleCallback?.(intervalFn, { timeout: 5 * 1000 })

  return () => {
    window.clearInterval(memoryInterval)
  }
}
